import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import InputField from "../../components/InputField";
import Button from "../../components/Button";
import Chip from "../../components/Chip";
import Modal from "../../components/Modal";
import {
  createTransaction,
  getPenalty,
  adjustPenalty,
  markPenaltyPaid,
} from "../../services/paymentService";
import { getTransactionReceipt } from "../../services/loanService";

type Option = {
  id: string;
  label: string;
  subtitle?: string;
};

type RawPayment = {
  dueDate?: string;
  amount?: string;
  status?: string;
  paymentDate?: string | null;
  raw?: any;
  id?: string | number;
  remainingNumeric?: number;
};
type Transaction = {
  transaction_id: number;
  loan_id: number;
  schedule_id: number;
  user_id: number;
  processed_by?: number | null;
  amount_paid: string;
  payment_mode: string;
  reference_number?: string | null;
  status: string;
  created_at: string;
  updated_at?: string;
};

function parseCurrencyToNumber(s?: string | number | null): number {
  if (s == null) return 0;
  const str = String(s);
  const cleaned = str.replace(/[^0-9.\-]+/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0]; // YYYY-MM-DD
};

export default function SinglePayment() {
  const navigate = useNavigate();
  const location = useLocation();

  const navState = (location.state ?? {}) as any;
  const incomingPayment = (navState.payment as RawPayment) ?? null;
  const prefillAmountNumeric =
    typeof navState.prefillAmountNumeric === "number"
      ? navState.prefillAmountNumeric
      : null;
  const loan_code =
    typeof navState.loan_code === "string" ? navState.loan_code : null;
  const [data, setData] = useState<RawPayment | null>(incomingPayment);
  const [originalAmount, setOriginalAmount] = useState<string>("");
  const [amount, setAmount] = useState<string>(""); // string to keep input behaviour simple
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [paidon, setPaidOn] = useState(getTodayDate());

  // modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [recordedPaymentOn, setRecordedPaymentOn] = useState<string | null>(
    null,
  );

  // payment method
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "UPI">("cash");

  // pending options (unchanged UI from your original)
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingSelectedIds, setPendingSelectedIds] = useState<string[]>([]);
  const multiSelect = true;
  const [selectedTransaction, setSelectedTransaction] = useState<
    Transaction | number | null
  >(null);
  const [downloading, setDownloading] = useState(false);
  const [penaltyEnabled, setPenaltyEnabled] = useState(false);

  // processing state for confirm flow
  const [confirming, setConfirming] = useState(false);

  // Resolve loan and schedule ids from raw payload (robust)
  function resolveScheduleAndLoan() {
    const raw = data?.raw ?? {};
    const schedule_id =
      raw.schedule_id ??
      raw.schedule?.schedule_id ??
      raw.schedule?.id ??
      raw.id ??
      null;
    const loan_id =
      raw.loan_id ??
      raw.schedule?.loan_id ??
      raw.loan?.loan_id ??
      raw.loanId ??
      raw.loan_id ??
      raw.loanId ??
      null;
    return { schedule_id, loan_id };
  }

  // Prefill amount on mount / when nav state changes.
  useEffect(() => {
    setData(incomingPayment);
    let initialNumeric: number | null = null;

    if (
      prefillAmountNumeric != null &&
      Number.isFinite(prefillAmountNumeric) &&
      prefillAmountNumeric > 0
    ) {
      initialNumeric = prefillAmountNumeric;
    } else if (
      incomingPayment?.remainingNumeric != null &&
      Number.isFinite(incomingPayment.remainingNumeric) &&
      incomingPayment.remainingNumeric > 0
    ) {
      initialNumeric = incomingPayment.remainingNumeric;
    } else if (incomingPayment?.amount) {
      const parsed = parseCurrencyToNumber(incomingPayment.amount);
      if (parsed > 0) {
        initialNumeric = parsed;
      }
    }

    if (initialNumeric != null) {
      setOriginalAmount(String(initialNumeric));
      setAmount(String(initialNumeric));
    } else {
      setOriginalAmount(
        incomingPayment?.amount
          ? String(parseCurrencyToNumber(incomingPayment.amount))
          : "",
      );
      setAmount("");
    }
  }, [incomingPayment, prefillAmountNumeric]);

  useEffect(() => {
    if (!data) return;
    getaPenalty();
  }, [data?.raw?.schedule_id, data?.raw?.loan_id, data]);

  const adjustDebounceRef = useRef<number | null>(null);

  function togglePendingOption(id: string) {
    if (!multiSelect) {
      setPendingSelectedIds((prev) => (prev.includes(id) ? [] : [id]));
      return;
    }
    setPendingSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function handleModalClose() {
    setShowSuccessModal(false);
    navigate(-1);
  }
  function handlePaymentModalClose() {
    setShowPaymentModal(false);
  }

  // small helpers for display
  const displayLoanId = data?.raw?.loanId ?? data?.raw?.loan_id ?? "-";
  const displayDue = data?.amount ?? data?.raw?.total_due ?? "₹ 0";
  const displayLoanCode = loan_code ?? "-";

  async function handleDownloadReceipt(tx: Transaction | number | null) {
    const txId = typeof tx === "number" ? tx : tx?.transaction_id;
    if (!txId) return;
    setDownloading(true);
    try {
      const res = await getTransactionReceipt(txId);
      if (!res.success)
        throw new Error(res.message ?? "Failed to download receipt");

      const blob = res.blob as Blob;

      const filenameFromHeader = (() => {
        try {
          const cd =
            res.headers?.["content-disposition"] ||
            res.headers?.["Content-Disposition"] ||
            "";
          const m1 = /filename\*=UTF-8''(.+)$/.exec(cd);
          if (m1 && m1[1]) return decodeURIComponent(m1[1]);
          const m2 = /filename=\"?([^\";]+)\"?/.exec(cd);
          if (m2 && m2[1]) return m2[1];
        } catch (e) {
          // ignore
        }
        return `receipt_${txId}.pdf`;
      })();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromHeader || `receipt_${txId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.message ?? "Failed to download receipt");
      toast.error(err?.message ?? "Failed to download receipt");
    } finally {
      setDownloading(false);
    }
  }

  // penalty state
  const [penaltyData, setPenaltyData] = useState<any | null>(null);
  const [adjustment, setAdjustment] = useState<number>(0);
  const [additional, setAdditional] = useState<number>(0);

  // helper to format INR
  function formatINR(n: number) {
    return `₹${Number(n || 0).toLocaleString("en-IN")}`;
  }

  // updated getaPenalty (returns response and updates state)
  async function getaPenalty() {
    const { schedule_id, loan_id } = resolveScheduleAndLoan();

    if (!loan_id || !schedule_id) {
      // console.warn("Missing loan_id or schedule_id for penalty calculation");
      return null;
    }

    const payload = {
      loan_id: String(loan_id),
      schedule_id: String(schedule_id),
    };

    try {
      setLoading(true);
      const resp = await getPenalty(payload);
      setPenaltyData(resp ?? null);

      if (
        resp?.penalty?.penalty_amount &&
        Number(resp.penalty.penalty_amount) > 0
      ) {
        setPenaltyEnabled(true);
      }
      return resp;
    } catch (err: any) {
      console.error("Failed to get penalty:", err?.response?.data ?? err);
      toast.error("Failed to fetch penalty details");
      return null;
    } finally {
      setLoading(false);
    }
  }

  // helper to call adjustPenalty and update local state
  async function callAdjustPenalty(
    penaltyId: string | number,
    penaltyAmount: number,
    reason?: string,
  ) {
    try {
      setLoading(true);
      const body = {
        penalty_amount: Number(penaltyAmount).toFixed(2),
        reason:
          reason ??
          `Adjustment via UI for schedule ${resolveScheduleAndLoan().schedule_id}`,
      };
      const adjResp = await adjustPenalty(penaltyId, body);
      if (adjResp?.penalty) {
        setPenaltyData((prev: any) => ({
          ...prev,
          penalty: { ...(prev?.penalty ?? {}), ...(adjResp?.penalty ?? {}) },
        }));
      } else {
        setPenaltyData((prev: any) => ({
          ...prev,
          penalty: { ...(prev?.penalty ?? {}), penalty_amount: penaltyAmount },
        }));
      }
      return adjResp;
    } catch (e: any) {
      console.error("callAdjustPenalty error:", e);
      const msg =
        e?.response?.data?.message ?? e?.message ?? "Failed to adjust penalty";
      toast.error(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }

  // compute total penalty (with adjustment and additional)
  function computeTotalPenaltyFromState(penaltyAmountFromServer?: number) {
    const baseServer = Number(
      penaltyAmountFromServer ?? penaltyData?.penalty?.penalty_amount ?? 0,
    );
    return Math.max(
      0,
      baseServer - Number(adjustment || 0) + Number(additional || 0),
    );
  }

  // schedule a debounced check when adjustment/additional change; if computed penalty becomes 0, call adjustPenalty(0)
  function scheduleAdjustCheck() {
    if (adjustDebounceRef.current) {
      window.clearTimeout(adjustDebounceRef.current);
      adjustDebounceRef.current = null;
    }
    adjustDebounceRef.current = window.setTimeout(async () => {
      try {
        const penaltyId =
          penaltyData?.penalty?.penalty_id ?? penaltyData?.penalty?.id ?? null;
        const computed = computeTotalPenaltyFromState();
        const serverPenaltyAmount = Number(
          penaltyData?.penalty?.penalty_amount ?? 0,
        );
        if (penaltyId && computed <= 0 && serverPenaltyAmount > 0) {
          await callAdjustPenalty(
            penaltyId,
            0,
            `Waived by adjustment fields when creating payment for schedule ${resolveScheduleAndLoan().schedule_id}`,
          );
        }
      } catch (e) {}
    }, 700);
  }

  // cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (adjustDebounceRef.current) {
        window.clearTimeout(adjustDebounceRef.current);
      }
    };
  }, []);

  // handler for checkbox toggle
  async function handlePenaltyToggle(checked: boolean) {
    setPenaltyEnabled(checked);

    const penaltyId =
      penaltyData?.penalty?.penalty_id ?? penaltyData?.penalty?.id ?? null;
    const serverPenaltyAmount = Number(
      penaltyData?.penalty?.penalty_amount ?? 0,
    );

    if (!checked) {
      // user unchecked: if server has a penalty > 0, set it to 0 immediately
      if (penaltyId && serverPenaltyAmount > 0) {
        try {
          // await callAdjustPenalty(penaltyId, 0, `Waived by user when creating payment for schedule ${resolveScheduleAndLoan().schedule_id}`);
        } catch (err) {
          return;
        }
      } else {
        setPenaltyData((prev: any) => ({
          ...prev,
          penalty: { ...(prev?.penalty ?? {}), penalty_amount: 0 },
        }));
      }
    } else {
      await getaPenalty();
    }
  }

  // compute total penalty and total payable for display
  const totalPenalty = computeTotalPenaltyFromState();
  const totalPayable =
    parseCurrencyToNumber(amount) + (penaltyEnabled ? totalPenalty : 0);

  async function handleConfirmPayment() {
    setErrorMessage(null);
    setSuccessMessage(null);
    setConfirming(true);

    if (!data) {
      setErrorMessage("No payment data available.");
      toast.error("No payment data available.");
      setConfirming(false);
      return;
    }

    const { schedule_id, loan_id } = resolveScheduleAndLoan();
    if (!schedule_id || !loan_id) {
      setErrorMessage(
        "Missing schedule_id or loan_id. Check payment.raw for identifiers.",
      );
      toast.error("Missing schedule_id or loan_id.");
      setConfirming(false);
      return;
    }

    const numericAmount = parseCurrencyToNumber(amount);

    try {
      // Refresh latest penalty BEFORE taking action so penalty adjustments/marking use current server state.
      const latestPenaltyResp = await getaPenalty();
      // derive penalty id / amount from latest response (fallback to local state)
      const penaltyId =
        latestPenaltyResp?.penalty?.penalty_id ??
        latestPenaltyResp?.penalty?.id ??
        penaltyData?.penalty?.penalty_id ??
        penaltyData?.penalty?.id ??
        null;
      const serverPenaltyAmount = Number(
        latestPenaltyResp?.penalty?.penalty_amount ??
          penaltyData?.penalty?.penalty_amount ??
          0,
      );

      // 2) If user UNCHECKED penalty and server has non-zero penalty, ensure it's set to zero
      if (!penaltyEnabled && penaltyId && serverPenaltyAmount > 0) {
        try {
          await callAdjustPenalty(
            penaltyId,
            0,
            `Waived by user when creating payment for schedule ${schedule_id}`,
          );
        } catch (adjErr: any) {
          console.error("Failed to set penalty to zero:", adjErr);
          setErrorMessage("Failed to update penalty");
          toast.error("Failed to update penalty");
          setConfirming(false);
          return;
        }
      }

      // 3) If user CHECKED penalty, ensure server penalty matches computed amount (adjust but don't mark paid yet)
      const computedTotalPenalty = computeTotalPenaltyFromState();
      if (penaltyEnabled && penaltyId) {
        try {
          await callAdjustPenalty(
            penaltyId,
            computedTotalPenalty,
            `Adjusted before payment for schedule ${schedule_id}`,
          );
        } catch (adjErr: any) {
          console.error("Penalty adjust failed:", adjErr);
          setErrorMessage("Failed to adjust penalty");
          toast.error("Failed to adjust penalty");
          setConfirming(false);
          return;
        }
      }

      // 4) Validate base amount numericAmount against remaining/pending (only base)
      const remainingCandidates = [
        Number((data as any)?.remainingNumeric ?? NaN),
        parseCurrencyToNumber(
          (data as any)?.raw?.remaining_amount ??
            (data as any)?.raw?.total_due ??
            (data as any)?.raw?.due_amount,
        ),
        parseCurrencyToNumber(originalAmount),
      ].filter(Number.isFinite) as number[];
      const remaining =
        remainingCandidates.length > 0 ? remainingCandidates[0] : NaN;

      // 5) Create transaction -- send only base amount (numericAmount)
      const paymentPayload: any = {
        schedule_id: Number(schedule_id),
        loan_id: Number(loan_id),
        amount_paid: numericAmount, // <-- only base amount
        payment_mode: paymentMethod === "UPI" ? "UPI" : "Cash",
        paid_on: paidon
          ? new Date(paidon).toISOString()
          : new Date().toISOString(),
      };

      setLoading(true);
      const resp = await createTransaction(paymentPayload);

      if (resp?.schedule) {
        setData((prev) => ({
          ...(prev ?? {}),
          raw: { ...(prev?.raw ?? {}), ...resp.schedule },
          ...resp.schedule,
        }));
      }
      const paymentDateFromServer =
        resp?.schedule?.paid_on ??
        resp?.transaction?.created_at ??
        paymentPayload.paid_on;
      setRecordedPaymentOn(paymentDateFromServer ?? null);
      setSelectedTransaction(resp.transaction ?? resp.transaction_id ?? null);

      // 6) After transaction success: if penaltyEnabled & penaltyId, mark penalty paid
      if (penaltyId) {
        try {
          const markResp = await markPenaltyPaid(penaltyId);
          setPenaltyData((prev: any) => ({
            ...prev,
            penalty: { ...(prev?.penalty ?? {}), ...(markResp?.penalty ?? {}) },
          }));
        } catch (markErr: any) {
          console.error("Mark penalty paid failed:", markErr);
          const msg =
            markErr?.response?.data?.message ??
            markErr?.message ??
            "Failed to mark penalty as paid";
          setErrorMessage((prev) => (prev ? prev + " | " : "") + msg);
          toast.error(msg);
        }
      }

      // success
      setSuccessMessage("Payment successful.");
      toast.success("Payment successful.");
      setShowPaymentModal(false);
      setShowSuccessModal(true);
    } catch (err: any) {
      console.error("Payment flow error:", err);
      const serverMsg = err?.response?.data?.message ?? err?.message;
      if (
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("exceeds pending")
      ) {
        setErrorMessage(serverMsg);
        toast.error(serverMsg);
      } else {
        const msg = serverMsg ?? "Payment failed";
        setErrorMessage(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  }
  return (
    <div className="pt-3 ">
      <div className="flex items-center justify-between light-bg-color rounded-b-md  sticky top-0 p-2 mb-4 z-10">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-white hover:bg-gray-200 text-gray-700 font-medium shadow-sm transition-all duration-150 cursor-pointer"
          >
            <Icon icon="mdi:arrow-left" width={18} />
            <span className="hidden md:block ">Back</span>
          </button>

          <h3 className="text-lg font-semibold text-color">Make a Payment</h3>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 m-2  grid grid-cols-2">
        <div className="col-span-2 md:col-span-1 flex flex-col ">
          <div className="rounded-t-lg ">
            <div>
              <h3 className="text-lg font-semibold mb-4 ">Payment Summary</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 ">
              <p className="text-gray-600 mb-3">Loan Code : </p>
              <p className="text-gray-800 font-medium mb-3">
                {displayLoanCode}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 ">
              <p className="text-gray-600 mb-3">Due Amount : </p>
              <p className="text-gray-800 font-medium mb-3">{displayDue}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 ">
              <p className="text-gray-600 mb-3">Due Date : </p>
              <p className="text-gray-800 font-medium mb-3">
                {data?.dueDate ?? data?.raw?.due_date ?? "-"}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 ">
              <p className="text-gray-600 mb-3">Payment On : </p>
              <p className="text-gray-800 font-medium mb-3">
                {(data?.raw?.paid_on &&
                  new Date(data.raw.paid_on).toLocaleString("en-IN")) ??
                  "Not paid yet"}
              </p>
            </div>
          </div>

          <div className="w-full   mt-4  mb-3">
            <div className="mt-4 ">
              <h3 className="text-lg font-semibold mb-4 ">Payment Details</h3>
            </div>

            <p className="text-sm text-gray-700 mb-2">
              {" "}
              {`Amount to Pay (Remaining  : ${originalAmount || "—"})
            `}
              <span className="text-red-500">*</span>
            </p>
            <div className="md:pe-5">
              <InputField
                type="number"
                placeholder="Enter Amount to Pay"
                value={amount}
                onChange={(e: any) => setAmount(e.target.value)}
                required
              />
              <p className="text-sm text-gray-700 mb-2 mt-3">
                Paid On
                <span className="text-red-500">*</span>
              </p>
              <InputField
                type="date"
                value={paidon}
                onChange={(e: any) => setPaidOn(e.target.value)}
                required
                className="mt-2"
              />
            </div>
          </div>
        </div>

        <div className="col-span-2 md:col-span-1 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <input
              id="penalty-checkbox"
              type="checkbox"
              checked={penaltyEnabled}
              onChange={(e) => handlePenaltyToggle(e.target.checked)}
              className="w-4 h-4 accent-indigo-600"
            />
            <label htmlFor="penalty-checkbox" className="text-lg font-semibold">
              Penalty Summary
            </label>
          </div>

          <div
            className={`w-full  flex flex-col transition-all duration-200 ${
              penaltyEnabled ? "" : "opacity-50 pointer-events-none select-none"
            }`}
          >
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 shadow-sm">
              <div className="border-b border-gray-100 mb-3 pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600">Due Penalty :</p>
                    <p className="text-2xl font-bold text-indigo-600 mt-1">
                      {formatINR(
                        Number(penaltyData?.penalty?.penalty_amount ?? 0),
                      )}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-gray-500 text-xs">Days late</p>
                    <p className="text-sm font-medium">
                      {penaltyData?.days_late
                        ? penaltyData.days_late.toFixed(2)
                        : "-"}
                    </p>
                  </div>
                </div>

                <div className="mt-3">
                  <p className="text-gray-600">Reason :</p>
                  <p className="text-sm text-indigo-600 font-medium mt-1">
                    {penaltyData?.penalty?.reason ?? "—"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Percent penalty: {penaltyData?.percent_penalty ?? "-"}{" "}
                    &middot; Penalty Per Day:{" "}
                    {penaltyData?.per_day_penalty
                      ? formatINR(Number(penaltyData.per_day_penalty))
                      : "-"}
                  </p>
                </div>
              </div>

              <label
                htmlFor="settlement-discount"
                className="block text-xs text-gray-600 mb-2"
              >
                Adjustment Amount
              </label>
              <div className="flex items-center">
                <InputField
                  id="settlement-discount"
                  type="number"
                  placeholder="0"
                  aria-label="Discount amount"
                  disabled={!penaltyEnabled}
                  value={adjustment}
                  onChange={(e) => {
                    setAdjustment(Number(e.target.value || 0));
                    scheduleAdjustCheck();
                  }}
                />
              </div>

              <label
                htmlFor="settlement-additional"
                className="block text-xs text-gray-600 my-2"
              >
                Additional Amount
              </label>
              <div className="flex items-center">
                <InputField
                  id="settlement-additional"
                  type="number"
                  placeholder="0"
                  aria-label="Additional amount"
                  disabled={!penaltyEnabled}
                  value={additional}
                  onChange={(e) => {
                    setAdditional(Number(e.target.value || 0));
                    scheduleAdjustCheck();
                  }}
                />
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500">Total Penalty</p>
                <div className="mt-2">
                  <p className="text-2xl font-bold ">
                    {formatINR(totalPenalty)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment details */}
        <div className="col-span-2 md:col-span-2 border-t border-gray-300">
          <div className="flex items-start justify-start flex-col">
            <div className="mt-4 text-center w-full flex justify-center flex-col">
              <p className="text-xl text-gray-800">Total Payable Amount</p>
              <p className="text-xs text-gray-400">
                Base: {formatINR(parseCurrencyToNumber(amount))}
                {penaltyEnabled && <> + Penalty: {formatINR(totalPenalty)}</>}
              </p>
              <div className=" ">
                <p className="text-2xl font-bold text-indigo-600">
                  {formatINR(totalPayable)}
                </p>
              </div>

              <div className="flex gap-3 items-center justify-center bg-transparent py-4 flex-wrap ">
                <Chip
                  label="Cash"
                  selected={paymentMethod === "cash"}
                  icon="streamline-freehand:money-cash-bill"
                  onClick={() => setPaymentMethod("cash")}
                />
                <Chip
                  label="UPI"
                  selected={paymentMethod === "UPI"}
                  icon="mdi:credit-card-outline"
                  onClick={() => setPaymentMethod("UPI")}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-6 flex justify-center gap-3 col-span-2">
          <div className="w-full md:w-2/5">
            <Button
              variant="primary"
              className="w-full"
              onClick={() => setShowPaymentModal(true)}
              disabled={loading}
            >
              {loading ? "Processing..." : "Make Payment"}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirm Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        title="Confirm Payment"
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600">
              You are about to make a payment. Review the amounts below:
            </p>
            <div className="mt-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Base Amount</span>
                <span className="font-medium">
                  {formatINR(parseCurrencyToNumber(amount))}
                </span>
              </div>

              {penaltyEnabled && (
                <>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-600">Penalty</span>
                    <span className="font-medium">
                      {formatINR(totalPenalty)}
                    </span>
                  </div>
                </>
              )}

              <div className="flex justify-between mt-2 border-t pt-2">
                <span className="text-lg font-semibold">Total Payable</span>
                <span className="text-lg font-semibold">
                  {formatINR(totalPayable)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 items-center justify-end">
            <div className="w-full">
              <Button
                variant="secondary"
                className="w-full"
                onClick={handlePaymentModalClose}
                disabled={confirming || loading}
              >
                Cancel
              </Button>
            </div>
            <div className="w-full">
              <Button
                variant="primary"
                className="w-full"
                onClick={handleConfirmPayment}
                disabled={confirming || loading}
              >
                {confirming || loading
                  ? "Processing..."
                  : `Pay ${formatINR(totalPayable)}`}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        title="Payment Successful"
      >
        <div className="flex flex-col items-center justify-center text-center space-y-4 py-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>

          <p className="text-lg font-medium text-gray-700">
            Payment of {formatINR(totalPayable)} was successful!
          </p>

          {recordedPaymentOn && (
            <p className="text-sm text-gray-500">
              Recorded on: {new Date(recordedPaymentOn).toLocaleString("en-IN")}
            </p>
          )}

          <div className="pt-2 flex justify-center gap-5 w-full">
            <Button
              onClick={() => handleDownloadReceipt(selectedTransaction)}
              disabled={downloading}
              variant="secondary"
            >
              {downloading ? "Downloading (PDF)..." : "Download Receipt (PDF)"}
            </Button>
            <Button onClick={handleModalClose} variant="primary">
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
